import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Card from '../../atoms/Card';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import '@splidejs/react-splide/css';
import RightIcon from '../../atoms/Icons/RightIcon';
import showDataSC from 'ui/lib/mc/showDataSC';
import eventViewServicePDP from 'ui/lib/gtm/eventViewServicePDP';
import eventVariantService from 'ui/lib/gtm/eventVariantService';
import { removeDuplicates } from 'ui/helpers/utils/getDefaultCarAndService';
import { useRouter } from '../../../hooks/useCustomRouter';
import { setSelectedPackage, setServicePackageState } from '../../../store/servicePackage/actions';
import dynamic from 'next/dynamic';
import { IBreadcrumbs } from '../Breadcrumbs';
import useImpressionCouponService from '../../../hooks/emarsys/coupon-service/useImpressionCouponService';
import eventClickCouponService from '../../../helpers/emarsys/coupon-service/eventClickCouponService';
import useImpressionPackageService from '../../../hooks/emarsys/package-service/useImpressionPackageService';
import eventClickPackageService from '../../../helpers/emarsys/package-service/eventClickPackageService';

const DiscIcon = dynamic(() => import('../../atoms/Icons/DiscIcon'), {
  ssr: false,
});
const CalenderIcon = dynamic(() => import('../../atoms/Icons/CalenderIcon'), {
  ssr: false,
});
const VoucherCard = dynamic(() => import('../../atoms/VoucherCard'), {
  ssr: false,
});
const TagIcon = dynamic(() => import('../../atoms/Icons/TagIcon'), {
  ssr: false,
});
const PercentIcon = dynamic(() => import('../../atoms/Icons/PercentIcon'), {
  ssr: false,
});

const ServicePackageSplide = dynamic(() => import('../../atoms/SevicePackageSplide'), {
  ssr: false,
  loading: () => <div className="lg:hidden h-[88px] bg-gray-200 rounded-lg animate-pulse" />,
});

interface ServiceDetailOptionsProps {
  titleChoosePackage: string;
  showVoucherModal: (isOpen: boolean) => void;
  setViewPackageCarModal: (isOpen: boolean) => void;
  setViewTypeCar: (isOpen: boolean) => void;
  serviceType: string;
  isCMS?: boolean;
  serviceContent: {
    couponPacketServiceContent?: {
      titleVoucher?: string;
      descriptionVoucher?: string;
      carCategory?: string;
    };
  };
  showSelectService?: boolean;
  triggerGTM?: {
    status: boolean;
  };
  serviceProductTitle?: string;
  bodyTypeName?: string;
  serviceProductList?: any[];
  viewTypeCar?: boolean;
  serviceDiscounts?: any[];
  emarsysData?: {
    pageTitle: string;
    breadcrumbItems: IBreadcrumbs[];
  };
  feature?: string;
}

const ServiceDetailOptions = ({
  titleChoosePackage,
  showVoucherModal,
  setViewPackageCarModal,
  setViewTypeCar,
  serviceType,
  isCMS = false,
  serviceContent,
  showSelectService = true,
  triggerGTM,
  serviceProductTitle,
  bodyTypeName,
  serviceProductList = [],
  serviceDiscounts,
  emarsysData = {
    pageTitle: '',
    breadcrumbItems: [],
  },
  feature,
}: ServiceDetailOptionsProps) => {
  const router = useRouter();
  const dispatch = useDispatch();

  const carTypeRef = useRef(null);
  const packageRef = useRef(null);

  const { selectedCar, selectedPackage } = useSelector((state: any) => state.servicePackage);
  const { userData } = useSelector((state: any) => state.auth) || {};
  const { isMobileScreen } = useSelector((state: any) => state.page);
  const pageState = useSelector((state: any) => state.page); // Select the entire page state
  const [showMore, setShowMore] = useState<boolean>(false);
  const [isFloatingMenuVisible, setIsFloatingMenuVisible] = useState(false);
  const [isPackageServiceVisible, setIsPackageServiceVisible] = useState(false);
  const { categoryService } = useSelector((state: any) => state.dataGTM || {});
  const packageServiceRef = useRef(null);
  const initialPackageServicePosition = useRef(0);

  // Define service list (package/coupon) from selectedCar
  const serviceList = useMemo(
    () =>
      serviceType === 'package'
        ? removeDuplicates(selectedCar?.servicePackage)
        : removeDuplicates(selectedCar?.couponServices),
    [selectedCar]
  );

  const isTabletScreen = useMemo(
    () => pageState.screenSize.width >= 768 && pageState.screenSize.width < 1024,
    [pageState.screenSize.width]
  );

  useEffect(() => {
    // Reset initia initialPackageServicePosition when initial load / change page
    initialPackageServicePosition.current = 0;

    const handleScroll = () => {
      const currentPackageServicePosition =
        packageServiceRef?.current?.getBoundingClientRect()?.top || 0;
      const scrollDifference =
        initialPackageServicePosition.current - currentPackageServicePosition;
      // Jika perubahan posisi lebih dari 100px, atur setIsPackageServiceVisible menjadi false
      if (
        scrollDifference > 25 &&
        currentPackageServicePosition <= initialPackageServicePosition.current
      ) {
        setIsPackageServiceVisible(true);
      } else {
        setIsPackageServiceVisible(false);
        setIsFloatingMenuVisible(false);
      }
    };

    // Tambahkan event listener untuk mendengarkan perubahan scroll
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleSelectCarType = (e) => {
    e.preventDefault();
    if (feature === 'COUPON_SERVICE') {
      eventClickCouponService(
        dispatch,
        selectedPackage,
        selectedCar,
        emarsysData.pageTitle,
        emarsysData.breadcrumbItems,
        userData?.customerNo,
        'tipe mobil',
        'pilih tipe mobil'
      );
    } else if (feature === 'PACKAGE_SERVICE') {
      eventClickPackageService(
        dispatch,
        selectedPackage,
        selectedCar,
        emarsysData.pageTitle,
        emarsysData.breadcrumbItems,
        userData?.customerNo,
        'tipe mobil',
        'pilih tipe mobil'
      );
    }
    // Open modal
    setViewTypeCar(true);
  };

  const handleSelectCarFromFloatingMenu = (e) => {
    e.preventDefault();
    // Open modal
    setIsFloatingMenuVisible(true);
    setViewTypeCar(true);
  };

  const handleRedirectServicePage = (selectedServiceCode) => {
    const { carCategory, productCode } = router.query;
    const vehicleCode = selectedCar?.vehicleCode?.toLowerCase();
    const serviceCode = selectedServiceCode.toLowerCase();
    const redirectPath = `/${carCategory}/${productCode}/${serviceCode}?vehicleCode=${vehicleCode}`;

    router.push(redirectPath);
    return;
  };

  const handleSelectService = (selectedCode) => {
    const selectedPackageTemp = selectedCar[
      serviceType === 'package' ? 'servicePackage' : 'couponServices'
    ]?.find((item) => item?.code === selectedCode);

    if (feature === 'COUPON_SERVICE') {
      eventClickCouponService(
        dispatch,
        selectedPackageTemp,
        selectedCar,
        emarsysData.pageTitle,
        emarsysData.breadcrumbItems,
        userData?.customerNo,
        'pilih kupon servis',
        'pilih kupon servis'
      );
    } else if (feature === 'PACKAGE_SERVICE') {
      eventClickPackageService(
        dispatch,
        selectedPackageTemp,
        selectedCar,
        emarsysData.pageTitle,
        emarsysData.breadcrumbItems,
        userData?.customerNo,
        'pilih paket servis',
        'pilih paket servis'
      );
    }
    if (serviceType === 'coupon') {
      handleRedirectServicePage(selectedCode);
    }

    dispatch(setSelectedPackage(selectedPackageTemp));
    if (serviceType === 'package') {
      dispatch(
        setServicePackageState({
          field: 'isShowBaseModel',
          value: false,
        })
      );
    }
    eventVariantService(
      categoryService,
      serviceProductTitle,
      selectedCar?.vehicleName,
      selectedPackageTemp?.name,
      serviceType
    );
    if (triggerGTM?.status) {
      eventViewServicePDP(
        {
          name: serviceProductTitle.toLowerCase() || 'not available',
          id: serviceProductTitle.toLowerCase() || 'not available',
          price: Math.floor(
            selectedPackageTemp?.strikeThroughPrice?.value || selectedPackageTemp?.price?.value || 0
          ),
          brand: 'toyota',
          category: !bodyTypeName
            ? 'not available'
            : bodyTypeName.toLowerCase() === 'commercial'
              ? 'komersial'
              : bodyTypeName?.toLowerCase(),
          variant: selectedPackageTemp?.name?.toLowerCase() || 'not available',
          discountedPrice: Math.floor(
            selectedPackageTemp?.price?.value - selectedPackageTemp?.strikeThroughPrice?.value || 0
          ),
        },
        selectedCar?.vehicleName?.toLowerCase(),
        serviceType,
        categoryService
      );
      showDataSC(
        serviceProductTitle.toLowerCase(),
        selectedCar?.vehicleName,
        selectedPackageTemp?.name,
        selectedPackageTemp?.strikeThroughPrice?.value || selectedPackageTemp?.price?.value
      );
    }
  };

  const handleShowMore = (e) => {
    setShowMore(!showMore);
  };

  const sanitizedHtml = (string: string) => {
    const regex = /(<([^>]+)>)/gi;
    return string.replace(regex, '');
  };

  if (feature === 'COUPON_SERVICE') {
    useImpressionCouponService(
      carTypeRef,
      emarsysData.pageTitle,
      emarsysData.breadcrumbItems,
      'tipe mobil'
    );

    useImpressionCouponService(
      packageRef,
      emarsysData.pageTitle,
      emarsysData.breadcrumbItems,
      'pilih kupon servis'
    );
  } else if (feature === 'PACKAGE_SERVICE') {
    useImpressionPackageService(
      carTypeRef,
      emarsysData.pageTitle,
      emarsysData.breadcrumbItems,
      'tipe mobil'
    );
    useImpressionPackageService(
      packageRef,
      emarsysData.pageTitle,
      emarsysData.breadcrumbItems,
      'pilih paket servis'
    );
  }

  return (
    <>
      {/* Service Voucher */}
      {serviceDiscounts && serviceDiscounts?.length > 0 && (
        <div className="w-full p-4 lg:px-0">
          <button
            className="block w-full"
            onClick={isCMS ? () => {} : () => showVoucherModal(true)}
          >
            <div className="flex overflow-x-hidden lg:px-0">
              <div className="relative basis-12 grow-0 shrink-0">
                <div className="w-full h-full">
                  <DiscIcon />
                </div>
                <p className="absolute top-[50%] left-[50%] -translate-x-1/2 -translate-y-1/2 ml-0.5">
                  <PercentIcon />
                </p>
              </div>
              <VoucherCard className="w-full py-3 pl-3 pr-5" borderColor="#E9E9E9">
                <div className="flex justify-between items-center self-stretch  h-full">
                  <div className="text-start">
                    <p className="text-xs font-semibold text-reliableBlack90 mb-0.5 uppercase lg:text-sm lg:mb-1">
                      {serviceContent?.couponPacketServiceContent?.titleVoucher}
                    </p>
                    <p className="text-[8px] text-reliableBlack60 line-clamp-1 md:text-xs">
                      {serviceContent?.couponPacketServiceContent?.descriptionVoucher}
                    </p>
                  </div>
                  <div>
                    <RightIcon />
                  </div>
                </div>
              </VoucherCard>
            </div>
          </button>
        </div>
      )}
      {showSelectService ? (
        <>
          {/* Choose Package & Car */}
          <div className="w-full p-4 bg-[#F8F8F8] lg:p-5">
            {/* Choose car type */}
            <div className="relative mb-5 lg:mb-6" ref={carTypeRef}>
              <label
                className="text-xs font-bold text-reliableBlack mb-2 lg:text-sm"
                id="label-car-type"
                htmlFor="input-car-type"
              >
                Pilih Tipe Mobil
              </label>
              <input
                id="input-car-type"
                className="relative w-full py-3 px-4 text-sm font-semibold text-reliableBlack80 bg-[#FFFEFE]  border border-[#E9E9E9] rounded cursor-pointer"
                value={
                  typeof window === 'undefined'
                    ? serviceProductList?.[0]?.vehicleName
                    : selectedCar?.vehicleName || 'Pilih Tipe Mobil'
                }
                readOnly
                onClick={isCMS ? () => {} : (e) => handleSelectCarType(e)}
              />
              <span className="inline-block absolute right-0 top-[66%] -translate-y-1/2 mr-4 pointer-events-none">
                <RightIcon />
              </span>
            </div>

            {/* Choose service package */}
            <div style={{ maxWidth: '100vw' }} ref={packageRef}>
              <div className="flex justify-between items-center mb-2">
                <p className="text-xs font-bold text-reliableBlack lg:text-sm">
                  {titleChoosePackage} :
                </p>
                {(isMobileScreen || isTabletScreen) && (
                  <button
                    className="text-supportiveRed text-xs font-semibold"
                    onClick={isCMS ? () => {} : () => setViewPackageCarModal(true)}
                  >
                    Lihat Semua
                  </button>
                )}
              </div>

              {/* Fallback for disabled javascript */}
              {typeof window === 'undefined' && (
                <noscript>
                  <div className="noscript-slider">
                    {serviceProductList?.[0]?.couponServices?.map((item, index) => (
                      <Card
                        key={index}
                        idCard={`card-${serviceType}-${item?.name}`}
                        className={`h-full w-full px-3 py-2 border rounded ${item.code !== selectedPackage?.code ? 'bg-[#FFFEFE] border-[#E9E9E9]' : 'bg-[#FFE9E9] border-supportiveRed'}`}
                      >
                        <p
                          className={`text-xs font-semibold text-wrap mb-[6px] ${item.code !== selectedPackage?.code ? 'text-reliableBlack' : 'text-supportiveRed'}`}
                        >
                          {serviceType === 'package' ? item?.typeOfService : item?.name}
                        </p>

                        {serviceType === 'package' && (
                          <div className="flex items-center mb-[6px]">
                            <div className="shrink-0">
                              <TagIcon />
                            </div>

                            <p
                              className={`text-[10px] text-wrap overflow-hidden ml-1 text-reliableBlack70 line-clamp-2 text-ellipsis`}
                            >
                              {sanitizedHtml(item?.description)}
                            </p>
                          </div>
                        )}

                        <div className="flex items-center text-sm mb-0">
                          <CalenderIcon />
                          <p className={`text-[10px] ml-2 text-reliableBlack70`}>
                            Berlaku{' '}
                            {serviceType === 'package'
                              ? item?.yearRange
                              : item?.dayOfService + ' Hari'}
                          </p>
                        </div>
                      </Card>
                    ))}
                  </div>
                </noscript>
              )}
              {/* Mobile Screen */}
              <div ref={packageServiceRef}>
                <ServicePackageSplide
                  serviceList={serviceList}
                  selectedPackage={selectedPackage}
                  isCMS={isCMS}
                  handleSelectService={handleSelectService}
                  serviceType={serviceType}
                  sanitizedHtml={sanitizedHtml}
                />
              </div>

              {/* Desktop Screen */}
              <div
                className={`overflow-y-hidden hidden gap-2.5 no-scrollbar lg:grid lg:grid-cols-3 lg:gap-2 lg:grid-flow-row`}
                style={{ gridAutoColumns: `calc(100% / 2 - 0.625rem)` }}
              >
                {serviceList?.slice(0, showMore ? serviceList?.length : 6).map((item, index) => (
                  <label
                    key={`servicePackage${index}`}
                    htmlFor={`servicePackage${index}`}
                    className="inline-block cursor-pointer"
                  >
                    <input
                      id={`servicePackage${index}`}
                      type="checkbox"
                      className="peer sr-only"
                      value={item.code}
                      checked={item.code === selectedPackage}
                      onChange={isCMS ? () => {} : (e) => handleSelectService(e.target.value)}
                    />
                    <Card
                      idCard={`card-${serviceType}-${item.code}`}
                      className={`h-full px-3 py-2 border rounded ${item.code !== selectedPackage?.code ? 'bg-[#FFFEFE] border-[#E9E9E9]' : 'bg-[#FFE9E9] border-supportiveRed'}`}
                    >
                      <p
                        className={`text-xs font-semibold text-wrap mb-[6px] ${item.code !== selectedPackage?.code ? 'text-reliableBlack' : 'text-supportiveRed'}`}
                      >
                        {serviceType === 'package' ? item?.typeOfService : item?.name}
                      </p>

                      {/* Only show in service pakcage */}
                      {serviceType === 'package' && (
                        <div className="flex items-center mb-[6px]">
                          <div className="shrink-0">
                            <TagIcon />
                          </div>

                          <p
                            className={`text-[10px]  text-wrap overflow-hidden ml-1 line-clamp-2 text-ellipsis ${item.code !== selectedPackage?.code ? 'text-reliableBlack70' : 'text-supportiveRed'}`}
                          >
                            {sanitizedHtml(item?.description)}
                          </p>
                        </div>
                      )}

                      <div className="flex items-center text-sm mb-0">
                        <CalenderIcon />
                        <p
                          className={`text-[10px] ml-2 ${item.code !== selectedPackage?.code ? 'text-reliableBlack70' : 'text-supportiveRed'}`}
                        >
                          Berlaku{' '}
                          {serviceType === 'package'
                            ? item?.yearRange
                            : item?.dayOfService + ' Hari'}
                        </p>
                      </div>
                    </Card>
                  </label>
                ))}
              </div>

              {/* Showmore button */}
              {serviceList?.length > 6 && (
                <div className="hidden lg:flex mt-4">
                  {showMore ? (
                    <button
                      className="text-supportiveRed text-xs font-semibold ml-auto cursor-pointer"
                      onClick={isCMS ? () => {} : (e) => handleShowMore(e)}
                    >
                      Lihat Lebih Sedikit
                    </button>
                  ) : (
                    <button
                      className="text-supportiveRed text-xs font-semibold ml-auto cursor-pointer"
                      onClick={isCMS ? () => {} : (e) => handleShowMore(e)}
                    >
                      Lihat Lebih Banyak
                    </button>
                  )}
                  <div
                    className={`transform rotate-90 ${showMore ? 'rotate-[270deg]' : 'rotate-90'} ml-2 transition-all duration-300`}
                  >
                    <RightIcon />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Floating package service */}
          {(isMobileScreen || isTabletScreen) && (
            <div
              className={`${!isPackageServiceVisible && !isFloatingMenuVisible ? 'hidden' : 'block'} fixed top-0 left-0 z-[100] w-screen bg-white px-3.5 py-3 shadow-lg`}
            >
              {/* Choose car type */}
              <div className="relative mb-2">
                <input
                  className="relative w-full py-1.5 text-sm font-semibold text-reliableBlack80 bg-transparent border-none cursor-pointer"
                  value={selectedCar?.vehicleName || 'Pilih Tipe Mobil'}
                  readOnly
                  onClick={isCMS ? () => {} : (e) => handleSelectCarFromFloatingMenu(e)}
                />
                <span className="inline-block absolute right-0 top-[50%] -translate-y-1/2 rotate-90 mr-4 pointer-events-none">
                  <RightIcon />
                </span>
              </div>

              <Splide
                aria-label="Carousel"
                hasTrack={false}
                options={{
                  type: 'slide',
                  arrows: false,
                  pagination: false,
                  perPage: 1,
                  autoWidth: true,
                  gap: 6,
                }}
              >
                <div className="justify-center">
                  <SplideTrack>
                    {serviceList?.map((item, idx) => (
                      <SplideSlide key={`packageServices-data-id-${idx}`} role="presentation">
                        <label
                          htmlFor={`packageServices${idx}`}
                          className="inline-block cursor-pointer"
                        >
                          <input
                            id={`packageServices${idx}`}
                            type="checkbox"
                            className="peer sr-only"
                            value={item.code}
                            checked={item.code === selectedPackage?.code}
                            onChange={
                              isCMS
                                ? () => {}
                                : (e) => {
                                    setIsFloatingMenuVisible(true);
                                    handleSelectService(e.target.value);
                                  }
                            }
                          />
                          <Card
                            key={idx}
                            className={`h-full w-full px-3 py-2 border rounded ${item.code !== selectedPackage?.code ? 'bg-[#FFFEFE] border-[#E9E9E9]' : 'bg-[#FFE9E9] border-supportiveRed'}`}
                          >
                            <p
                              className={`text-xs font-semibold text-wrap mb-[6px] ${item.code !== selectedPackage?.code ? 'text-reliableBlack' : 'text-supportiveRed'}`}
                            >
                              {serviceType === 'package' ? item?.typeOfService : item?.name}
                            </p>
                            {/* Only show in service package */}
                            {serviceType === 'package' && (
                              <div className="flex items-center mb-[6px]">
                                <TagIcon />
                                <p
                                  className={`text-[10px] text-wrap ml-1 line-clamp-2 text-ellipsis ${item.code !== selectedPackage?.code ? 'text-reliableBlack70' : 'text-supportiveRed'}`}
                                >
                                  {item?.typeOfService}
                                </p>
                              </div>
                            )}
                            <div className="flex items-center text-sm mb-0">
                              <CalenderIcon />
                              <p
                                className={`text-[10px] ml-2 ${item.code !== selectedPackage?.code ? 'text-reliableBlack70' : 'text-supportiveRed'}`}
                              >
                                Berlaku{' '}
                                {serviceType === 'package'
                                  ? item?.yearRange
                                  : item?.dayOfService + ' Hari'}
                              </p>
                            </div>
                          </Card>
                        </label>
                      </SplideSlide>
                    ))}
                  </SplideTrack>
                </div>
              </Splide>
            </div>
          )}
        </>
      ) : null}
    </>
  );
};

export default ServiceDetailOptions;
